import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Package, Clock, AlertCircle, Search, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { toast } from 'react-hot-toast';
import logo from '../assets/images/arka-saydam-logo.png';
import BrandAutocomplete from '../components/BrandAutocomplete';
import ShoppingItemCard from '../components/ShoppingItemCard';
import {
    resolveDraftNormalization,
    resolveDraftNormalizationPreview
} from '../shared/shoppingNormalization';

const resolvePreferredBrand = (item = {}) => (
    String(item.preferredBrand || item.recommendedBrand || '').trim()
);

const resolveAlternativeBrand = (item = {}) => (
    String(item.alternativeProduct || '').trim()
);

const MARKET_STYLE_UNITS = new Set(['paket', 'kutu', 'koli', 'şişe', 'sise', 'file', 'demet']);
const SHARED_UNIT_OPTIONS = ['adet', 'kg', 'gr', 'lt', 'ml', 'paket', 'demet', 'kutu', 'koli', 'şişe'];

const normalizeUnitKey = (value = '') => (
    String(value || '')
        .trim()
        .toLocaleLowerCase('tr-TR')
);

const isWeightUnit = (unit = '') => ['g', 'gr', 'gram', 'kg'].includes(normalizeUnitKey(unit));
const isVolumeUnit = (unit = '') => ['ml', 'lt', 'l', 'litre'].includes(normalizeUnitKey(unit));
const isCountUnit = (unit = '') => ['adet', 'ad', 'tane'].includes(normalizeUnitKey(unit));

const resolveCanonicalUnit = (item = {}) => (
    String(
        item.canonicalUnit ||
        item.canonicalSourceSnapshot?.resolvedCanonicalUnit ||
        item.canonicalSourceSnapshot?.userAnswerUnit ||
        item.unit ||
        'adet'
    ).trim() || 'adet'
);

const resolveMarketUnit = (item = {}) => (
    String(
        item.marketUnit ||
        item.canonicalSourceSnapshot?.marketUnit ||
        item.editableUnit ||
        item.unit ||
        'adet'
    ).trim() || 'adet'
);

const resolvePerUnitCanonicalAmount = (item = {}) => {
    const explicit = Number(item.perUnitCanonicalAmount || 0);
    if (explicit > 0) return explicit;

    const snapshotAnswer = Number(item.canonicalSourceSnapshot?.userAnswer || 0);
    if (snapshotAnswer > 0) return snapshotAnswer;

    const marketAmount = Number(item.marketAmount || item.canonicalSourceSnapshot?.marketAmount || 0);
    const canonicalAmount = Number(item.canonicalAmount || item.canonicalSourceSnapshot?.resolvedCanonicalAmount || 0);
    if (marketAmount > 0 && canonicalAmount > 0) {
        return Number((canonicalAmount / marketAmount).toFixed(4));
    }

    return 0;
};

const convertAmountToCanonical = ({ amount, selectedUnit, canonicalUnit, perUnitCanonicalAmount }) => {
    const numericAmount = Number(amount || 0);
    if (!(numericAmount > 0)) return 0;

    const normalizedSelectedUnit = normalizeUnitKey(selectedUnit);
    const normalizedCanonicalUnit = normalizeUnitKey(canonicalUnit);

    if (MARKET_STYLE_UNITS.has(normalizedSelectedUnit)) {
        const multiplier = Number(perUnitCanonicalAmount || 0);
        if (!(multiplier > 0)) return 0;
        return Number((numericAmount * multiplier).toFixed(2));
    }

    if (normalizedCanonicalUnit === 'gr' || normalizedCanonicalUnit === 'g' || normalizedCanonicalUnit === 'gram') {
        if (normalizedSelectedUnit === 'kg') return Number((numericAmount * 1000).toFixed(2));
        return Number(numericAmount.toFixed(2));
    }

    if (normalizedCanonicalUnit === 'ml') {
        if (normalizedSelectedUnit === 'lt' || normalizedSelectedUnit === 'l' || normalizedSelectedUnit === 'litre') {
            return Number((numericAmount * 1000).toFixed(2));
        }
        return Number(numericAmount.toFixed(2));
    }

    return Number(numericAmount.toFixed(2));
};

const convertCanonicalToEditable = ({ finalAmount, selectedUnit, canonicalUnit, perUnitCanonicalAmount }) => {
    const numericFinalAmount = Number(finalAmount || 0);
    if (!(numericFinalAmount > 0)) return '';

    const normalizedSelectedUnit = normalizeUnitKey(selectedUnit);
    const normalizedCanonicalUnit = normalizeUnitKey(canonicalUnit);

    if (MARKET_STYLE_UNITS.has(normalizedSelectedUnit)) {
        const multiplier = Number(perUnitCanonicalAmount || 0);
        if (!(multiplier > 0)) return '';
        return Number((numericFinalAmount / multiplier).toFixed(2));
    }

    if ((normalizedCanonicalUnit === 'gr' || normalizedCanonicalUnit === 'g' || normalizedCanonicalUnit === 'gram') && normalizedSelectedUnit === 'kg') {
        return Number((numericFinalAmount / 1000).toFixed(2));
    }

    if (normalizedCanonicalUnit === 'ml' && (normalizedSelectedUnit === 'lt' || normalizedSelectedUnit === 'l' || normalizedSelectedUnit === 'litre')) {
        return Number((numericFinalAmount / 1000).toFixed(2));
    }

    return Number(numericFinalAmount.toFixed(2));
};

const buildEditableState = (item = {}) => {
    const canonicalUnit = resolveCanonicalUnit(item);
    const marketUnit = resolveMarketUnit(item);
    const preferredUnit = item.editableUnit || marketUnit || canonicalUnit || 'adet';
    const perUnitCanonicalAmount = resolvePerUnitCanonicalAmount(item);
    const finalAmount = Number(item.finalAmount ?? item.canonicalAmount ?? item.marketAmount ?? item.suggestedAmount ?? 0);
    const normalization = resolveDraftNormalization({
        name: item.ingredientName || item.name,
        unit: preferredUnit
    });
    const editableAmount = convertCanonicalToEditable({
        finalAmount,
        selectedUnit: preferredUnit,
        canonicalUnit: normalization.expectedCanonicalUnit || canonicalUnit,
        perUnitCanonicalAmount
    });

    return {
        editableUnit: preferredUnit,
        editableAmount,
        perUnitCanonicalAmount: perUnitCanonicalAmount > 0 ? String(perUnitCanonicalAmount) : '',
        expectedCanonicalUnit: normalization.expectedCanonicalUnit || canonicalUnit,
        measurementType: normalization.measurementType,
        canonicalUnit,
        marketUnit,
        marketAmount: Number(editableAmount || 0) > 0 ? Number(editableAmount) : Number(item.marketAmount || 0)
    };
};

const resolveEditableAmountMeta = (item = {}) => {
    return {
        amount: item.editableAmount ?? '',
        unit: item.editableUnit || resolveMarketUnit(item),
        multiplier: resolvePerUnitCanonicalAmount(item) || 1
    };
};

const formatEditableAmount = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '';
    return String(Number.isInteger(numericValue) ? numericValue : numericValue);
};

const SharedMarketList = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completing, setCompleting] = useState(false);

    // Track item details being edited before confirmation
    const [editingItem, setEditingItem] = useState(null);
    const isStandalone = typeof window !== 'undefined' && (
        window.matchMedia?.('(display-mode: standalone)')?.matches ||
        window.navigator?.standalone === true
    );
    const sharedRequestConfig = {
        headers: {
            'X-BBTV-Share-Context': 'guest'
        }
    };

    useEffect(() => {
        fetchSharedList();
    }, [token]);

    const openInBrowser = () => {
        window.open(window.location.href, '_blank', 'noopener,noreferrer');
    };

    const fetchSharedList = async (attempt = 0) => {
        const item = null;
        const fetchGuard = { needsClarification: false, prompt: '' };
        if (false) {
            
        
        
            toast.error(normalization.prompt || 'Lütfen paket karşılığını gir.');
            return;
        }
        const normalization = resolveDraftNormalization({
            name: item?.ingredientName || item?.name,
            unit: item?.editableUnit || item?.marketUnit || item?.unit || 'adet'
        });
        if (normalization.needsClarification && !(Number(item?.perUnitCanonicalAmount || 0) > 0)) {
            toast.error(normalization.prompt || 'Lütfen paket karşılığını gir.');
            return;
        }
        try {
            setLoading(true);
            setError(null);
            console.log('--- DEBUG: API_BASE_URL is:', API_BASE_URL, 'token is:', token);
            const requestUrl = `${API_BASE_URL}/api/shared-shopping/${token}`;
            console.log('--- DEBUG: Requesting URL:', requestUrl);
            const response = await axios.get(requestUrl, sharedRequestConfig);
            console.log('--- DEBUG: response.data is:', response.data);

            if (!response.data || !Array.isArray(response.data.items)) {
                throw new Error(response.data?.error || ('Geçersiz liste verisi: ' + JSON.stringify(response.data)));
            }

            // Pre-fill recommended brands if nothing else is set
            const enrichedItems = response.data.items.map(item => ({
                ...item,
                preferredBrand: item.brand || item.recommendedBrand || '',
                brand: item.isChecked ? (item.brand || '') : '',
                alternativeProduct: item.alternativeProduct || '',
                ...buildEditableState(item)
            }));
            setList({ ...response.data, items: enrichedItems });
            setLoading(false);
        } catch (err) {
            console.error('Error fetching shared list:', err);
            const status = err.response?.status;
            const isNetworkLike = !err.response || err.code === 'ERR_NETWORK';

            // Only retry if it is a genuine network/axios error, not a JS programming error (TypeError/Error)
            const isRealNetworkError = axios.isAxiosError(err) && (!err.response || err.code === 'ERR_NETWORK');

            if (isRealNetworkError && attempt === 0) {
                setTimeout(() => {
                    fetchSharedList(1);
                }, 1200);
                return;
            }

            setLoading(false);

            if (status === 410) {
                setError('Bu paylaşım linkinin süresi dolmuş veya liste tamamlanmış.');
            } else if (status === 404) {
                setError('Liste bulunamadı.');
            } else if (isStandalone && isNetworkLike) {
                setError('Link uygulama içinde açılamadı. Tarayıcıda tekrar denemek daha sağlıklı olabilir.');
            } else {
                setError(err.message || 'Liste yüklenirken bir hata oluştu.');
            }
        }
    };

    const handleConfirmItem = async (itemId) => {
        const item = list.items.find(i => i.id === itemId);
        const hasBrandOptions = Boolean(resolvePreferredBrand(item) || resolveAlternativeBrand(item));
        if (hasBrandOptions && !String(item?.brand || '').trim()) {
            toast.error('Hangi markayı aldığını seç veya yaz.');
            return;
        }
        const normalization = resolveDraftNormalization({
            name: item?.ingredientName || item?.name,
            unit: item?.editableUnit || item?.marketUnit || item?.unit || 'adet'
        });
        if (normalization.needsClarification && !(Number(item?.perUnitCanonicalAmount || 0) > 0)) {
            toast.error(normalization.prompt || 'Lütfen paket karşılığını gir.');
            return;
        }
        try {
            await axios.patch(`${API_BASE_URL}/api/shared-shopping/${token}/item/${itemId}`, {
                isChecked: true,
                brand: item.brand,
                alternativeProduct: item.alternativeProduct,
                finalAmount: item.finalAmount || item.suggestedAmount,
                price: item.price || 0,
                marketAmount: Number(item.editableAmount || item.marketAmount || 0) || 0,
                marketUnit: item.editableUnit || item.marketUnit || item.unit || 'adet',
                canonicalAmount: Number(item.finalAmount || item.canonicalAmount || 0) || 0,
                canonicalUnit: item.expectedCanonicalUnit || item.canonicalUnit || item.unit || 'adet',
                canonicalSourceSnapshot: {
                    ...(item.canonicalSourceSnapshot || {}),
                    marketAmount: Number(item.editableAmount || item.marketAmount || 0) || 0,
                    marketUnit: item.editableUnit || item.marketUnit || item.unit || 'adet',
                    userAnswer: Number(item.perUnitCanonicalAmount || 0) || null,
                    userAnswerUnit: item.expectedCanonicalUnit || item.canonicalUnit || item.unit || 'adet',
                    resolvedCanonicalAmount: Number(item.finalAmount || item.canonicalAmount || 0) || 0,
                    resolvedCanonicalUnit: item.expectedCanonicalUnit || item.canonicalUnit || item.unit || 'adet',
                    measurementType: normalization.measurementType,
                    source: normalization.needsClarification ? 'USER_PROMPT' : (item.canonicalSourceSnapshot?.source || 'DIRECT_INPUT')
                }
            }, sharedRequestConfig);

            // Update local state
            setList(prev => ({
                ...prev,
                items: prev.items.map(i =>
                    i.id === itemId ? { ...i, isChecked: true } : i
                )
            }));
            setEditingItem(null);
        } catch (err) {
            console.error('Error confirming item:', err);
            toast.error(err.response?.data?.error || 'Ürün güncellenemedi');
        }
    };

    const handleToggleItem = async (itemId, currentStatus) => {
        if (currentStatus) {
            // Uncheck is simple
            try {
                await axios.patch(`${API_BASE_URL}/api/shared-shopping/${token}/item/${itemId}`, {
                    isChecked: false
                }, sharedRequestConfig);
                setList(prev => ({
                    ...prev,
                    items: prev.items.map(item =>
                        item.id === itemId ? { ...item, isChecked: false } : item
                    )
                }));
            } catch (err) {
                toast.error('Durum güncellenemedi');
            }
        } else {
            // Checking needs details
            const targetItem = list?.items?.find((item) => item.id === itemId);
            if (targetItem) {
                void handleOpenItemEditor(targetItem);
            }
        }
    };

    const handleItemChange = (itemId, field, value) => {
        setList(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.id === itemId ? { ...item, [field]: value } : item
            )
        }));
    };

    const updateEditableItem = (itemId, updater) => {
        setList((prev) => ({
            ...prev,
            items: prev.items.map((item) => {
                if (item.id !== itemId) return item;
                return typeof updater === 'function'
                    ? updater(item)
                    : { ...item, ...updater };
            })
        }));
    };

    const handleBrandChoice = (itemId, brandValue) => {
        handleItemChange(itemId, 'brand', brandValue);
    };

    const handleEditableAmountChange = (item, value) => {
        if (value === '') {
            updateEditableItem(item.id, {
                editableAmount: '',
                finalAmount: '',
                canonicalAmount: 0,
                marketAmount: 0
            });
            return;
        }

        const parsedValue = Number(value);
        if (!Number.isFinite(parsedValue) || parsedValue < 0) {
            return;
        }

        const canonicalUnit = item.expectedCanonicalUnit || resolveCanonicalUnit(item);
        const finalAmount = convertAmountToCanonical({
            amount: parsedValue,
            selectedUnit: item.editableUnit || resolveMarketUnit(item),
            canonicalUnit,
            perUnitCanonicalAmount: item.perUnitCanonicalAmount
        });

        updateEditableItem(item.id, {
            editableAmount: parsedValue,
            finalAmount,
            canonicalAmount: finalAmount,
            canonicalUnit,
            marketAmount: parsedValue,
            marketUnit: item.editableUnit || resolveMarketUnit(item)
        });
    };

    const handleEditableUnitChange = (item, nextUnit) => {
        const normalization = resolveDraftNormalization({
            name: item.ingredientName || item.name,
            unit: nextUnit
        });
        const nextCanonicalUnit = normalization.expectedCanonicalUnit || resolveCanonicalUnit(item);
        const nextPerUnitCanonicalAmount = normalization.needsClarification
            ? item.perUnitCanonicalAmount || ''
            : '';
        const nextEditableAmount = convertCanonicalToEditable({
            finalAmount: item.finalAmount ?? item.canonicalAmount ?? item.suggestedAmount,
            selectedUnit: nextUnit,
            canonicalUnit: nextCanonicalUnit,
            perUnitCanonicalAmount: nextPerUnitCanonicalAmount
        });
        const nextFinalAmount = nextEditableAmount === ''
            ? item.finalAmount
            : convertAmountToCanonical({
                amount: nextEditableAmount,
                selectedUnit: nextUnit,
                canonicalUnit: nextCanonicalUnit,
                perUnitCanonicalAmount: nextPerUnitCanonicalAmount
            });

        updateEditableItem(item.id, {
            editableUnit: nextUnit,
            editableAmount: nextEditableAmount,
            perUnitCanonicalAmount: nextPerUnitCanonicalAmount,
            expectedCanonicalUnit: nextCanonicalUnit,
            measurementType: normalization.measurementType,
            marketUnit: nextUnit,
            marketAmount: Number(nextEditableAmount || 0) || 0,
            finalAmount: nextFinalAmount,
            canonicalAmount: Number(nextFinalAmount || 0) || 0,
            canonicalUnit: nextCanonicalUnit
        });
    };

    const handlePerUnitCanonicalAmountChange = (item, value) => {
        const nextFinalAmount = value === ''
            ? ''
            : convertAmountToCanonical({
                amount: item.editableAmount,
                selectedUnit: item.editableUnit || resolveMarketUnit(item),
                canonicalUnit: item.expectedCanonicalUnit || resolveCanonicalUnit(item),
                perUnitCanonicalAmount: value
            });

        updateEditableItem(item.id, {
            perUnitCanonicalAmount: value,
            finalAmount: nextFinalAmount,
            canonicalAmount: Number(nextFinalAmount || 0) || 0,
            marketAmount: Number(item.editableAmount || 0) || 0
        });
    };

    const handleOpenItemEditor = async (item) => {
        setEditingItem(item.id);

        const selectedUnit = item.editableUnit || resolveMarketUnit(item);
        const normalization = resolveDraftNormalization({
            name: item.ingredientName || item.name,
            unit: selectedUnit
        });
        if (!normalization.needsClarification || Number(item.perUnitCanonicalAmount || 0) > 0 || !item.dictionaryId) {
            return;
        }

        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) return;

            const params = new URLSearchParams({
                dictionaryId: String(item.dictionaryId),
                ingredientName: String(item.ingredientName || item.name || ''),
                marketUnit: selectedUnit
            });
            const response = await fetch(`${API_BASE_URL}/api/shopping/unit-preference?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${authToken}`
                }
            });

            if (!response.ok) return;
            const data = await response.json();
            const preference = data?.preference || null;
            if (!preference?.multiplier) return;

            updateEditableItem(item.id, {
                expectedCanonicalUnit: preference.canonicalUnit || normalization.expectedCanonicalUnit || resolveCanonicalUnit(item),
                perUnitCanonicalAmount: String(preference.multiplier),
                finalAmount: convertAmountToCanonical({
                    amount: item.editableAmount,
                    selectedUnit,
                    canonicalUnit: preference.canonicalUnit || normalization.expectedCanonicalUnit || resolveCanonicalUnit(item),
                    perUnitCanonicalAmount: preference.multiplier
                })
            });
        } catch (error) {
            console.error('Shared unit preference fetch error:', error);
        }
    };

    const handleResetItem = (itemId) => {
        setList(prev => ({
            ...prev,
            items: prev.items.map(item => {
                if (item.id !== itemId) return item;
                return {
                    ...item,
                    brand: '',
                    alternativeProduct: '',
                    finalAmount: item.suggestedAmount || '',
                    price: '',
                    note: null,
                    isChecked: false,
                    ...buildEditableState({
                        ...item,
                        brand: '',
                        alternativeProduct: '',
                        finalAmount: item.suggestedAmount || '',
                        price: ''
                    })
                };
            })
        }));
        setEditingItem(itemId);
    };

    const handleComplete = async () => {
        try {
            setCompleting(true);
            await axios.post(`${API_BASE_URL}/api/shared-shopping/${token}/complete`, {
                items: list.items.map((item) => ({
                    id: item.id,
                    dictionaryId: item.dictionaryId,
                    brand: item.brand || '',
                    alternativeProduct: item.alternativeProduct || '',
                    finalAmount: item.finalAmount || item.suggestedAmount || 0,
                    price: item.price || 0,
                    marketAmount: Number(item.editableAmount || item.marketAmount || 0) || 0,
                    marketUnit: item.editableUnit || item.marketUnit || item.unit || 'adet',
                    canonicalAmount: Number(item.finalAmount || item.canonicalAmount || 0) || 0,
                    canonicalUnit: item.expectedCanonicalUnit || item.canonicalUnit || item.unit || 'adet',
                    canonicalSourceSnapshot: {
                        ...(item.canonicalSourceSnapshot || {}),
                        marketAmount: Number(item.editableAmount || item.marketAmount || 0) || 0,
                        marketUnit: item.editableUnit || item.marketUnit || item.unit || 'adet',
                        userAnswer: Number(item.perUnitCanonicalAmount || 0) || null,
                        userAnswerUnit: item.expectedCanonicalUnit || item.canonicalUnit || item.unit || 'adet',
                        resolvedCanonicalAmount: Number(item.finalAmount || item.canonicalAmount || 0) || 0,
                        resolvedCanonicalUnit: item.expectedCanonicalUnit || item.canonicalUnit || item.unit || 'adet'
                    },
                    isChecked: item.isChecked,
                    note: item.note || null
                }))
            }, sharedRequestConfig);
            toast.success('Alışveriş tamamlandı! Şefe bildirildi.');

            // Show success message and redirect after delay
            setTimeout(() => {
                navigate('/');
            }, 3000);
        } catch (err) {
            console.error('Error completing shopping:', err);
            toast.error('Alışveriş tamamlanamadı');
            setCompleting(false);
        }
    };

    const checkedCount = list?.items?.filter(item => item.isChecked).length || 0;
    const totalCount = list?.items?.length || 0;
    const allChecked = checkedCount === totalCount && totalCount > 0;

    if (loading) {
        return (
            <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="relative w-20 h-20 mx-auto mb-8">
                        <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                    <h2 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Liste Hazırlanıyor...</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Şefin sepeti yola çıkmak üzere.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl p-10 max-w-md w-full text-center border border-slate-100 dark:border-slate-800">
                    <div className="w-20 h-20 bg-rose-50 dark:bg-rose-900/10 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-rose-100 dark:border-rose-900/20">
                        <AlertCircle className="text-rose-500" size={32} />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">Eyvah!</h2>
                    <p className="text-slate-500 dark:text-slate-400 mb-6 leading-relaxed font-medium">{error}</p>
                    <div className="space-y-3">
                        <button
                            onClick={() => fetchSharedList()}
                            className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-blue-600/20"
                        >
                            Tekrar Dene
                        </button>
                        {isStandalone && (
                            <button
                                onClick={openInBrowser}
                                className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-95 transition-all border border-slate-200 dark:border-slate-700"
                            >
                                Tarayıcıda Aç
                            </button>
                        )}
                        <button
                            onClick={() => navigate('/')}
                            className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-950 rounded-2xl font-black uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-slate-900/20 dark:shadow-white/10"
                        >
                            Ana Sayfaya Dön
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!list) {
        return null;
    }

    return (
        <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 pb-32">
            {/* Header section with Glassmorphism */}
            <div className="sticky top-0 z-30 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-800">
                <div className="max-w-4xl mx-auto px-6 py-4 sm:py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-6">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-3">
                                <img
                                    src={logo}
                                    alt="Logo"
                                    className="w-8 h-8 object-contain drop-shadow-sm"
                                />
                                <span className="text-sm font-black text-slate-900 dark:text-white tracking-widest uppercase">
                                    Bana Bir Tarif Ver
                                </span>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <h1 className="text-xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white leading-tight">
                                    {list.name}
                                </h1>
                                <div className="flex items-center gap-3 underline decoration-blue-500/30 decoration-2 underline-offset-4">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                                        Market Listesi
                                    </p>
                                    <span className="w-1 h-1 bg-slate-300 dark:bg-slate-700 rounded-full"></span>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                        {new Date(list.createdAt).toLocaleDateString('tr-TR')}
                                    </p>
                                </div>
                                {list.ownerContext?.ownerName && (
                                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                        {list.ownerContext.ownerName} tarafından paylaşıldı
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/10 rounded-2xl border border-blue-100 dark:border-blue-900/20">
                            <Package className="text-blue-600 dark:text-blue-400" size={16} />
                            <span className="text-[10px] font-black text-blue-700 dark:text-blue-300 uppercase tracking-widest">
                                Güvenli Paylaşım
                            </span>
                        </div>
                    </div>

                    {/* Compact Tracker */}
                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 rounded-2xl p-4 border border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-xl border transition-colors ${allChecked ? 'bg-green-500/10 border-green-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
                                <Check size={16} className={allChecked ? 'text-green-500' : 'text-blue-500'} />
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tamamlanan</p>
                                <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                                    {checkedCount} / {totalCount} Ürün
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter">
                                {totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0}%
                            </p>
                        </div>
                    </div>
                </div>

                {/* Visual Progress Bar (under header) */}
                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                        className={`h-full transition-all duration-700 ease-out ${allChecked ? 'bg-green-500' : 'bg-blue-600'}`}
                        style={{ width: `${totalCount > 0 ? (checkedCount / totalCount) * 100 : 0}%` }}
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-6 py-10">
                <div className="grid gap-4">
                    {list.items.map(item => {
                        const isEditing = editingItem === item.id;
                        const editableAmountMeta = resolveEditableAmountMeta(item);
                        return (
                            <div key={item.id} className="space-y-3">
                                <ShoppingItemCard
                                    item={{
                                        ...item,
                                        checked: item.isChecked
                                    }}
                                    onQuantityChange={(id, val) => handleItemChange(id, 'finalAmount', val)}
                                    onBrandChange={(id, val) => handleItemChange(id, 'brand', val)}
                                    onAlternativeProductChange={(id, val) => handleItemChange(id, 'alternativeProduct', val)}
                                    onPriceChange={(id, val) => handleItemChange(id, 'price', val)}
                                    onToggleCheck={(id) => handleToggleItem(id, item.isChecked)}
                                    mode="CHECK"
                                    viewMode="GUEST"
                                />

                                {isEditing && (
                                    <div className="rounded-[1.75rem] border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 shadow-lg shadow-slate-200/70">
                                        <div className="mb-4 flex items-start justify-between gap-4">
                                            <div>
                                                <p className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">
                                                    Ürünü Tamamla
                                                </p>
                                                <p className="mt-1 text-sm font-semibold text-slate-500">
                                                    İstersen marka ve ödenen tutarı gir, sonra ürünü tamamla.
                                                </p>
                                            </div>
                                            <div className="rounded-full bg-blue-100 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-blue-700">
                                                {item.ingredientName || item.name}
                                            </div>
                                        </div>

                                        {(resolvePreferredBrand(item) || resolveAlternativeBrand(item)) && (
                                            <div className="mb-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                                                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                                                    Alınan Marka Seçimi
                                                </p>
                                                <p className="mt-1 text-xs font-semibold text-slate-400">
                                                    Hangisini aldıysan ona dokun. Hiçbiri yoksa alttaki alana başka marka yaz.
                                                </p>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {resolvePreferredBrand(item) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleBrandChoice(item.id, resolvePreferredBrand(item))}
                                                            className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                                                                item.brand === resolvePreferredBrand(item)
                                                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                                                                    : 'border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600'
                                                            }`}
                                                        >
                                                            {resolvePreferredBrand(item)}
                                                        </button>
                                                    )}
                                                    {resolveAlternativeBrand(item) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleBrandChoice(item.id, resolveAlternativeBrand(item))}
                                                            className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                                                                item.brand === resolveAlternativeBrand(item)
                                                                    ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/20'
                                                                    : 'border border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:text-sky-600'
                                                            }`}
                                                        >
                                                            {resolveAlternativeBrand(item)} (Alternatif)
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleBrandChoice(item.id, '')}
                                                        className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                                                            !String(item.brand || '').trim()
                                                                ? 'bg-slate-900 text-white'
                                                                : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                                                        }`}
                                                    >
                                                        Başka Marka
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                            <label className="space-y-2">
                                                <span className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                                                    Alınan Marka
                                                </span>
                                                <input
                                                    type="text"
                                                    value={item.brand || ''}
                                                    placeholder="Örn. Tat, Migros"
                                                    onChange={(e) => handleItemChange(item.id, 'brand', e.target.value)}
                                                    className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                                                />
                                            </label>

                                            <label className="space-y-2">
                                                <span className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                                                    Alternatif Marka
                                                </span>
                                                <input
                                                    type="text"
                                                    value={item.alternativeProduct || ''}
                                                    placeholder="Yoksa bunu al"
                                                    onChange={(e) => handleItemChange(item.id, 'alternativeProduct', e.target.value)}
                                                    className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                                                />
                                            </label>

                                            <label className="space-y-2">
                                                <span className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                                                    Alınan Miktar
                                                </span>
                                                <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
                                                    <input
                                                        type="number"
                                                        value={formatEditableAmount(editableAmountMeta.amount)}
                                                        placeholder="Miktar"
                                                        onChange={(e) => handleEditableAmountChange(item, e.target.value)}
                                                        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                                                    />
                                                    <select
                                                        value={item.editableUnit || editableAmountMeta.unit || 'adet'}
                                                        onChange={(e) => handleEditableUnitChange(item, e.target.value)}
                                                        className="h-12 rounded-2xl border border-slate-200 bg-white px-3 text-xs font-black uppercase tracking-[0.14em] text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                                                    >
                                                        {SHARED_UNIT_OPTIONS.map((unitOption) => (
                                                            <option key={unitOption} value={unitOption}>
                                                                {unitOption}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </label>

                                            <label className="space-y-2">
                                                <span className="block text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                                                    Ödenen Tutar
                                                </span>
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        value={item.price || ''}
                                                        placeholder="0"
                                                        onChange={(e) => handleItemChange(item.id, 'price', e.target.value)}
                                                        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 pr-12 text-sm font-bold text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                                                    />
                                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-black text-emerald-600">
                                                        ₺
                                                    </span>
                                                </div>
                                                {Number(item.priceAtTime || 0) > 0 && (
                                                    <p className="text-xs font-semibold text-slate-400">
                                                        Tahmini: {Number(item.priceAtTime).toLocaleString('tr-TR')} ₺
                                                    </p>
                                                )}
                                            </label>
                                        </div>

                                        {resolveDraftNormalization({
                                            name: item.ingredientName || item.name,
                                            unit: item.editableUnit || editableAmountMeta.unit || 'adet'
                                        }).needsClarification && (
                                            <div className="mt-3 rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4">
                                                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-amber-700">
                                                    {resolveDraftNormalization({
                                                        name: item.ingredientName || item.name,
                                                        unit: item.editableUnit || editableAmountMeta.unit || 'adet'
                                                    }).prompt}
                                                </p>
                                                <div className="mt-3 grid grid-cols-[minmax(0,1fr)_90px] gap-2">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        value={item.perUnitCanonicalAmount || ''}
                                                        placeholder="Örn: 500"
                                                        onChange={(e) => handlePerUnitCanonicalAmountChange(item, e.target.value)}
                                                        className="h-12 rounded-2xl border border-amber-200 bg-white px-4 text-sm font-black text-slate-900 outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                                                    />
                                                    <div className="flex h-12 items-center justify-center rounded-2xl bg-white text-xs font-black uppercase tracking-[0.14em] text-amber-700">
                                                        {item.expectedCanonicalUnit || resolveCanonicalUnit(item)}
                                                    </div>
                                                </div>
                                                <p className="mt-3 text-xs font-semibold text-amber-800/80">
                                                    {resolveDraftNormalizationPreview({
                                                        ...item,
                                                        unit: item.editableUnit || editableAmountMeta.unit || 'adet',
                                                        amount: Number(item.editableAmount || 0) || 0,
                                                        perUnitCanonicalAmount: item.perUnitCanonicalAmount || ''
                                                    })?.text || `${item.ingredientName || item.name} için iç hesap ${item.expectedCanonicalUnit || resolveCanonicalUnit(item)} bazında yapılacak.`}
                                                </p>
                                            </div>
                                        )}

                                        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                                            <button
                                                onClick={() => handleConfirmItem(item.id)}
                                                className="h-14 flex-1 rounded-2xl bg-blue-600 px-6 text-base font-black uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 active:scale-[0.99] sm:h-12 sm:px-5 sm:text-sm"
                                            >
                                                {item.isChecked ? 'Güncelle' : 'Aldım'}
                                            </button>
                                            <button
                                                onClick={() => handleResetItem(item.id)}
                                                className="h-12 rounded-2xl border border-amber-200 bg-amber-50 px-5 text-sm font-black uppercase tracking-[0.18em] text-amber-700 transition hover:bg-amber-100"
                                            >
                                                Sıfırla
                                            </button>
                                            <button
                                                onClick={() => setEditingItem(null)}
                                                className="h-12 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-black uppercase tracking-[0.18em] text-slate-500 transition hover:bg-slate-50"
                                            >
                                                Vazgeç
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!isEditing && (
                                    <div className="flex justify-end">
                                        <button
                                            onClick={() => handleOpenItemEditor(item)}
                                            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-500 transition hover:border-blue-200 hover:text-blue-600"
                                        >
                                            {item.isChecked ? 'Düzenle' : 'Detay Gir'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Fixed Bottom Action Bar */}
            <div className="fixed bottom-0 left-0 right-0 p-6 z-40 bg-gradient-to-t from-white via-white dark:from-slate-950 dark:via-slate-950 to-transparent">
                <div className="max-w-4xl mx-auto">
                    <button
                        onClick={handleComplete}
                        disabled={completing || !allChecked}
                        className={`group relative w-full py-5 rounded-[2rem] font-black text-sm uppercase tracking-[0.2em] shadow-2xl transition-all duration-300 active:scale-[0.98] ${allChecked && !completing
                            ? 'bg-blue-600 text-white hover:bg-blue-700 hover:-translate-y-1 shadow-blue-600/30'
                            : 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                            }`}
                    >
                        <div className="flex items-center justify-center gap-4">
                            {completing ? (
                                <div className="flex items-center gap-3">
                                    <div className="animate-spin rounded-full h-5 w-5 border-4 border-white/30 border-t-white" />
                                    <span>Tamamlanıyor...</span>
                                </div>
                            ) : (
                                <>
                                    <span>{allChecked ? 'Alışverişi Bitir & Şefe Bildir' : `Ürünleri Tamamla (${checkedCount}/${totalCount})`}</span>
                                    <ArrowLeft size={18} className="rotate-180 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </div>
                    </button>
                    {!allChecked && (
                        <p className="text-center text-[10px] font-bold text-slate-400 dark:text-slate-600 mt-4 uppercase tracking-[0.1em]">
                            Listeyi tamamlamak için tüm ürünleri sepetine at şefim!
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SharedMarketList;
